import { useEffect, useMemo, useState } from 'react'

import { isPrivacyCenterContentLoading } from 'common/query-screen-status'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { isEmpty, map } from 'lodash'
import { makeTraceDetails } from 'common'
import { useScreenFinishLoadTac } from 'common/hooks/useScreenFinishLoadTac'
import { useLocation, useNavigate } from 'react-router-dom'
import { Routes } from 'common/constants'
import { logBridgeError, sendSelectContent } from 'common/helpers'

import { interWbNavigate } from '@interco/inter-webview-bridge'

import { useRequestTerms } from './useRequestTerms'
import { TTermSection } from '../types/useRequestTerms.types'
import { getTermIcon, shouldTermOpenAnURL } from '../utils'

export function usePrivacyPolicy() {
  const location = useLocation()
  const navigate = useNavigate()

  const from = location.state?.from

  const [traceDetails, setTraceDetails] = useState('')

  const [errorOrInactive, setErrorOrInactive] = useState(false)

  const [termSections, setTermSections] = useState<TTermSection[]>()

  const {
    isAppLoading,
    isAppError,
    isAppActive,
    privacyCenterContent,
    userSelectedEntity,
    userLanguage,
  } = useAppContext()

  const content = privacyCenterContent?.components?.privacyPolicy?.content
  const emptyPage = privacyCenterContent?.components?.privacyPolicyEmptyPage?.content
  const pdfContent = privacyCenterContent?.components?.pdfViewer?.content

  const isActive = privacyCenterContent?.components?.privacyPolicy?.isActive

  const { terms, status, notFoundTerms } = useRequestTerms()

  const hasPrivacyPolicyContent = !!content
  const isContentLoading = isPrivacyCenterContentLoading(isAppLoading, hasPrivacyPolicyContent)
  const isTermsPending = status === 'pending'

  const shouldSendPrivacyPolicyTac = useMemo(() => {
    if (isContentLoading || isTermsPending) return false
    const isError = status === 'error' || isAppError || isEmpty(terms) || isEmpty(content)
    const isInactive = !isActive || !isAppActive
    return !((isError || isInactive) && !notFoundTerms)
  }, [
    isContentLoading,
    isTermsPending,
    status,
    isAppError,
    terms,
    content,
    isActive,
    isAppActive,
    notFoundTerms,
  ])

  useScreenFinishLoadTac({
    name: 'PrivacyPolicy',
    screen: Routes.PRIVACY_POLICY,
    action: 'PrivacyPolicyFinishLoad',
    entity: userSelectedEntity,
    shouldSend: shouldSendPrivacyPolicyTac,
    logLabel: 'send privacy policy finish tac',
  })

  useEffect(() => {
    if (!isContentLoading && !isTermsPending) {
      const isError = status === 'error' || isAppError || isEmpty(terms) || isEmpty(content)
      const isInactive = !isActive || !isAppActive
      const isErrorOrInactive = (isError || isInactive) && !notFoundTerms

      setErrorOrInactive(isErrorOrInactive)

      if (isErrorOrInactive) {
        setTraceDetails(makeTraceDetails(!!isActive, content))
      }
    }
  }, [
    privacyCenterContent,
    status,
    terms,
    isAppActive,
    isContentLoading,
    content,
    errorOrInactive,
    isActive,
    isAppError,
    isTermsPending,
    notFoundTerms,
  ])

  useEffect(() => {
    if (!isContentLoading && !isTermsPending && !isEmpty(terms)) {
      const termArray = map(terms, (term) => {
        const { shouldOpenUrl, url } = shouldTermOpenAnURL({
          language: userLanguage,
          termId: term.type,
          userSelectedEntity,
        })
        const icon = getTermIcon(shouldOpenUrl)

        return {
          ...term,
          icon,
          onClick: async () => {
            try {
              await sendSelectContent({
                flow: 'privacy_policy',
                step: 'privacy_policy',
                screen_name: Routes.PRIVACY_POLICY,
                element_name: 'selectPrivacyPolice',
                element_type: 'button',
                parent_name: 'PrivacyPolicy',
                scenario: JSON.stringify({
                  screen: Routes.PRIVACY_POLICY,
                  product: userSelectedEntity,
                  ...term,
                  createdAt: `${term.createdAt}`,
                  updatedAt: `${term.updatedAt}`,
                  ref_type: 'button',
                  content_action: 'touch',
                }),
              })
            } catch (error: unknown) {
              logBridgeError('send privacy policy tic', error)
            }

            if (shouldOpenUrl) {
              interWbNavigate.openNativeBrowser(url)
            } else {
              const reportMetadata = {
                title: term.description,
                url: term.url,
                downloadingMessage: pdfContent?.downloadingMessage ?? '',
                errorMessage: pdfContent?.errorMessage ?? '',
              }

              interWbNavigate.openPdf(reportMetadata.title, reportMetadata.url)
            }
          },
        }
      })
      setTermSections(termArray)
    }
  }, [
    terms,
    isContentLoading,
    isTermsPending,
    userSelectedEntity,
    navigate,
    pdfContent,
    userLanguage,
  ])

  return {
    title: content?.title ?? '',
    from,
    traceDetails,
    errorOrInactive,
    termSections,
    isContentLoading,
    isTermsPending,
    notFoundTerms,
    emptyPage,
  }
}
